import {
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalProps,
  ModalRoot,
  ModalRootProps,
  Strong,
} from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';
import type { ComponentPropsWithRef } from 'react';
import { useEffect } from 'react';

import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';

import { DialogID, useDialog, useDialogUnsynced } from './store';
import './styles.scss';

type DialogUnwrappedProps = ModalRootProps &
  ModalProps & {
    open: boolean;
    onClose?: () => void;
    preventDismiss?: boolean;
  };

type DialogProps = Omit<
  DialogUnwrappedProps,
  'open' | 'loading' | 'loadingText' | 'heading'
> & {
  id: DialogID;
  heading?: string;
  loading?: boolean;
  loadingText?: string;
};

const DialogUnwrapped = ({
  children,
  className,
  open,
  onClose,
  heading = '',
  description = '',
  hideCloseButton = false,
  preventDismiss = false,
  ...props
}: DialogUnwrappedProps) => (
  <ModalRoot
    open={open}
    onOpenChange={(isOpen) => {
      if (!isOpen && onClose) {
        onClose();
      }
    }}
  >
    <Modal
      className={clsx('dialog__Root', className)}
      heading={heading}
      description={description}
      hideCloseButton={hideCloseButton}
      onPointerDownOutside={(event) => preventDismiss && event.preventDefault()}
      onEscapeKeyDown={(event) => preventDismiss && event.preventDefault()}
      onInteractOutside={(event) => preventDismiss && event.preventDefault()}
      {...props}
    >
      {children}
    </Modal>
  </ModalRoot>
);

interface ActionsProps extends ComponentPropsWithRef<'div'> {
  ignoreControl?: boolean;
}

const DialogActions = ({
  children,
  className,
  ignoreControl = false,
  ...props
}: ActionsProps) => {
  const { hasControl, appointment, isSyncedAppointment } =
    useAppointmentContext();

  return (
    <ModalFooter {...props} className={className}>
      {hasControl || ignoreControl || !isSyncedAppointment ? (
        <>{children}</>
      ) : (
        <Flex className="dialog-actions__waiting">
          <Strong>
            Waiting for {appointment?.controller?.name ?? 'controller'} to
            continue
          </Strong>
        </Flex>
      )}
    </ModalFooter>
  );
};

const Dialog = ({
  id,
  children,
  onClose,
  heading = '',
  description = '',
  loading = false,
  loadingText = '',
  ...props
}: DialogProps) => {
  const { open, setOpen } = useDialog(id);
  useEffect(() => () => setOpen(false), [setOpen]);

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <DialogUnwrapped
      {...props}
      open={open}
      onClose={handleClose}
      heading={heading}
      description={description}
      loading={loading}
      loadingText={loadingText}
    >
      {children}
    </DialogUnwrapped>
  );
};

const DialogUnsynced = ({
  id,
  children,
  onClose,
  heading = '',
  description = '',
  loading = false,
  loadingText = '',
  ...props
}: DialogProps) => {
  const { open, setOpen } = useDialogUnsynced(id);

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <DialogUnwrapped
      {...props}
      open={open}
      onClose={handleClose}
      heading={heading}
      description={description}
      loading={loading}
      loadingText={loadingText}
    >
      {children}
    </DialogUnwrapped>
  );
};

export {
  DialogUnwrapped,
  Dialog,
  ModalContent as DialogContent,
  DialogActions,
  DialogUnsynced,
};
export type { DialogProps, DialogUnwrappedProps };
export * from './store';
